import { AppBar, Card, CardContent, FormControlLabel, FormHelperText, Grid, Switch, TextField, Toolbar, Typography } from "@mui/material";
import { withStyles, WithStyles } from "@mui/styles";
import * as api from "api/Core";
import { FormStyleClasses, GlobalClassKeys } from "assets/jss/common-styles";
import ButtonCancel from "components/Buttons/ButtonCancel";
import ButtonSave from "components/Buttons/ButtonSave";
import IconButtonAdd from "components/Buttons/IconButtonAdd";
import { RequestIndicator } from "components/CallServer";
import RequestErrorDialog from "components/CallServer/RequestErrorDialog";
import { RequestErrorReport } from "components/CallServer/RequestErrorReport";
import IconApplyUserTest from "components/Icons/IconApplyUserTest";
import IconCalendar from "components/Icons/IconCalendar";
import IconCheckDouble from "components/Icons/IconCheckDouble";
import CardHeaderColor from "components/MuiExtras/Card/CardHeaderColor";
import MuiContainer from "components/MuiExtras/MuiContainer";
import MuiLink from "components/MuiExtras/MuiLink";
import MuiMuted from "components/MuiExtras/Typography/MuiMuted";
import { RecordAbout } from "components/RecordAbout";
import { WithSnackbar, withSnackbar } from 'components/SnackbarProvider/Snackbar';
import TextFieldNumeric from "components/TextFieldNumeric";
import { TextPluralization } from "components/TextPluralization";
import LibUtils from "lib/LibUtils";
import { GetSetupKnowledgeTestDto, SaveSetupKnowledgeTestDto, SetupKnowledgeTestBaseFields } from "models/ApiModels";
import { RequestLifeCycle } from "models/AppModels";
import * as React from "react";
import { useTranslation, WithTranslation, withTranslation } from "react-i18next";
import { Prompt } from "react-router";
import { ISetupProps } from "views/System/Components/SetupAdminEdit";
import SetupKnowledgeTestExceptionDialog from "views/System/Components/SetupKnowledgeTestExceptionDialog";
import SetupKnowledgeTestExceptionEditDialog from "views/System/Components/SetupKnowledgeTestExceptionEditDialog";
import SetupKnowledgeTestLogDialog from "views/System/Components/SetupKnowledgeTestLogDialog";

/////////////////////////////////////////////////////////////////////////////////////

interface SetupKnowledgeTestBaseFieldsCompProps {
    autoFocus: boolean;
    record: SetupKnowledgeTestBaseFields;
    handleChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
}

export const SetupKnowledgeTestBaseFieldsComp: React.FC<SetupKnowledgeTestBaseFieldsCompProps> = ({ autoFocus, record, handleChange }) => {
    const { t } = useTranslation();
    return (
        <div>
            <TextFieldNumeric
                name="questionsBySession"
                label={t("Views.System.Components.SetupKnowledgeTestEdit.QuestionBySessionLabel")}
                margin="normal"
                value={record.questionsBySession}
                onChange={handleChange}
                autoFocus={autoFocus}
                fullWidth
                required
                InputProps={
                    {
                        startAdornment: <IconApplyUserTest fixedWidth />
                    }
                }
            />

            <TextFieldNumeric
                name="retentionDaysInterval"
                label={t("Views.System.Components.SetupKnowledgeTestEdit.RetentionDaysLabel")}
                margin="normal"
                value={record.retentionDaysInterval}
                onChange={handleChange}
                fullWidth
                required
                InputProps={
                    {
                        startAdornment: <IconCalendar fixedWidth />
                    }
                }
            />

            <br />
            <br />

            <MuiContainer margin="normal">
                <FormControlLabel
                    control={
                        <Switch
                            name="showCorrectAnswer"
                            checked={record.showCorrectAnswer}
                            onChange={handleChange}
                            value="showCorrectAnswer"
                            color="secondary"
                            size="small"
                        />
                    }
                    label={t<string>("Views.System.Components.SetupKnowledgeTestEdit.ShowCorrectAnswerLabel")}
                />
            </MuiContainer>

            <div style={{ display: "none" }}>
                <MuiContainer margin="normal">
                    <Grid container justifyContent="center" alignItems="flex-start">
                        <Grid item>
                            <Switch
                                name="sequential"
                                checked={record.sequential}
                                onChange={handleChange}
                                value="sequential"
                                color="secondary"
                                size="small"
                            />
                        </Grid>
                        <Grid item xs>
                            <Typography variant="body2">{t("Views.System.Components.SetupKnowledgeTestEdit.SequentialSessionLabel")}</Typography>
                            <FormHelperText>{t("Views.System.Components.SetupKnowledgeTestEdit.SequentialSessionM1")}</FormHelperText>
                        </Grid>
                    </Grid>
                </MuiContainer>
            </div>
        </div>
    );
};

/////////////////////////////////////////////////////////////////////////////////////

interface IProps {
}

interface IState {
    dialog: any,
    formCycle: RequestLifeCycle,
    record: GetSetupKnowledgeTestDto,
    lastError: any;
}

type Props = ISetupProps & IProps & WithStyles<typeof FormStyleClasses> & WithSnackbar & WithTranslation;

/////////////////////////////////////////////////////////////////////////////////////

class SetupKnowledgeTestEdit extends React.Component<Props, IState> {

    private compareDto: any = null;

    ///
    constructor(props: Props) {
        super(props);

        this.compareDto = null;

        this.state = {
            formCycle: RequestLifeCycle.Init,
            dialog: null,
            record: null,
            lastError: null
        };

    }

    ///
    private res(r: string, f: any = null) {
        return this.props.t("Views.System.Components.SetupKnowledgeTestEdit." + r, f);
    }

    ///
    private load() {
        api.getSetupKnowledgeTest()
            .then((dto) => {
                this.compareDto = LibUtils.deepCopy(dto);

                this.setState({
                    formCycle: RequestLifeCycle.InitSuccess,
                    record: dto
                }, () => this.props.onEditing(false));
            })
            .catch((error: any) => {
                this.setState({
                    formCycle: RequestLifeCycle.InitFailed,
                    lastError: error
                });
            });
    }

    ///
    private getPostDto(): SaveSetupKnowledgeTestDto {
        const { record } = this.state;

        const postDto = {} as SaveSetupKnowledgeTestDto;

        LibUtils.copyProperties(record, postDto);

        return postDto;
    }

    ///
    private handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        this.updateRecord(
            event.target.name,
            event.target.type == "checkbox"
                ? (event.target as HTMLInputElement).checked
                : (event.target as HTMLInputElement).valueAsNumber
                    ? (event.target as HTMLInputElement).valueAsNumber
                    : event.target.value
        );
    };

    ///
    private updateRecord(name: string, v: any) {
        this.setState(state => {
            switch (name) {
                default:
                    state.record[name] = v;
            }
            return state;
        }, () => this.props.onEditing(this.isRecordChanged()));
    }

    ///
    private handleSubmit(event: React.MouseEvent<HTMLButtonElement>) {
        event.preventDefault();

        const dto = this.getPostDto();

        this.setState({
            formCycle: RequestLifeCycle.Posting,
        });

        api.saveSetupKnowledgeTest(dto)
            .then((response) => {
                this.compareDto = LibUtils.deepCopy(this.state.record);
                this.props.snackbar.showMessage(this.props.t("Common.Literals.UpdateSuccess"), "success");
                this.setState({ formCycle: RequestLifeCycle.PostSuccess }, () => this.props.onEditing(false));
            })
            .catch((err: any) => {
                this.setState({ lastError: err, formCycle: RequestLifeCycle.PostFailed });
            });
    }

    ///
    private handleAddException = () => {
        this.setState({
            dialog: <SetupKnowledgeTestExceptionEditDialog
                record={null}
                onClose={
                    (newRecord) => {
                        if (newRecord != null) {
                            this.setState((state) => {
                                state.record.exceptions = state.record.exceptions.concat(newRecord);
                                return { ...state, dialog: null };
                            });
                        }
                        else
                            this.setState({ dialog: null });
                    }
                }
            />
        });

    };

    ///
    private handleListExceptions = () => {
        this.setState({
            dialog: <SetupKnowledgeTestExceptionDialog
                exceptions={this.state.record.exceptions}
                onClose={
                    (newExceptions) => {
                        this.setState((state) => {
                            state.record.exceptions = newExceptions;
                            return { ...state, dialog: null };
                        });
                    }
                }
            />
        });
    };

    ///
    private isRecordChanged() {
        return this.state.lastError == null && !LibUtils.deepCompare(this.state.record, this.compareDto);
    }

    ///
    private renderToolbar() {
        return (
            <AppBar position="sticky" color="default" className={GlobalClassKeys.stickyToolbar} >
                <Toolbar>
                    <div className={GlobalClassKeys.pushRight}>
                        {
                            this.isRecordChanged() &&
                            <ButtonCancel
                                onClick={(e: React.MouseEvent<HTMLButtonElement>) => this.load()}
                            />
                        }
                        <ButtonSave
                            onClick={(e: React.MouseEvent<HTMLButtonElement>) => this.handleSubmit(e)}
                        />

                    </div>
                </Toolbar>
            </AppBar>
        );
    }

    ///
    private renderRecordInfo() {
        return this.state.record.recordInfo
            ? (
                <MuiContainer margin="normal">
                    <RecordAbout
                        record={this.state.record.recordInfo}
                        hideCreator={true}
                        onShowHistory={
                            () => {
                                this.setState({
                                    dialog:
                                        <SetupKnowledgeTestLogDialog
                                            onClose={() => this.setState({ dialog: null })}
                                        />
                                });
                            }
                        }
                    />
                </MuiContainer>
            )
            : null;
    }

    ///
    private renderFormRules() {
        const { record } = this.state;

        return (
            <MuiContainer margin="normal">

                <Card>
                    <CardHeaderColor color="primary" title={this.res("TestRulesTitle")} />
                    <CardContent>


                        <SetupKnowledgeTestBaseFieldsComp
                            autoFocus={false}
                            record={record}
                            handleChange={this.handleChange}
                        />

                        <Grid container justifyContent="center" alignItems="center"
                            spacing={0}>
                            <Grid item>
                                <IconButtonAdd color="warning" onClick={this.handleAddException} />
                            </Grid>
                            <Grid item xs>
                                <MuiLink color="secondary" onClick={this.handleListExceptions}>
                                    <span>{this.res("TestRuleExceptionsLink")}</span>
                                </MuiLink>
                                <Typography variant="subtitle2">
                                    <MuiMuted>
                                        <TextPluralization
                                            count={record.exceptions.length}
                                            resource="Views.System.Components.SetupKnowledgeTestEdit.TestRuleExceptionsText"
                                        />
                                    </MuiMuted>
                                </Typography>
                            </Grid>
                        </Grid>

                        <br />
                        <TextFieldNumeric
                            name="consecutiveCorrectAnswers"
                            label={this.res("ConsecutiveAnswersLabel")}
                            margin="dense"
                            value={record.consecutiveCorrectAnswers}
                            onChange={this.handleChange}
                            fullWidth
                            required
                            InputProps={
                                {
                                    startAdornment: <IconCheckDouble fixedWidth />
                                }
                            }
                        />

                    </CardContent>
                </Card>
            </MuiContainer >
        );
    }

    ///
    private renderFormFeedback() {
        const { record } = this.state;

        return (
            <MuiContainer margin="normal">

                <Card>
                    <CardHeaderColor color="primary" title={this.res("FeedbackTitle")} />
                    <CardContent>

                        <Grid container alignItems="flex-start">
                            <Grid item zeroMinWidth>
                                <Switch
                                    name="useDefaultFeedbacks"
                                    checked={record.useDefaultFeedbacks}
                                    onChange={this.handleChange}
                                    value="useDefaultFeedbacks"
                                    color="secondary"
                                    size="small"
                                />
                            </Grid>
                            <Grid item>
                                <Typography variant="body2">{this.res("FeedbackLabel")}</Typography>
                                <FormHelperText>{this.res("FeedbackM1")}</FormHelperText>
                            </Grid>
                        </Grid>

                        <br />

                        <TextField
                            name="feedbackCorrect"
                            label={this.res("FeedbackCorrectLabel")}
                            margin="dense"
                            value={record.feedbackCorrect || ""}
                            onChange={this.handleChange}
                            inputProps={{ maxLength: 255 }}
                            fullWidth
                            required
                        />

                        <TextField
                            name="feedbackIncorrect"
                            label={this.res("FeedbackIncorrectLabel")}
                            margin="dense"
                            value={record.feedbackIncorrect || ""}
                            onChange={this.handleChange}
                            inputProps={{ maxLength: 255 }}
                            fullWidth
                            required
                        />

                    </CardContent>
                </Card>
            </MuiContainer >
        );
    }

    ///
    public componentDidMount() {
        this.load();
    }

    ///
    public componentWillUnmount() {
        window.onbeforeunload = undefined;
    }

    ///
    public componentDidUpdate() {
        if (this.isRecordChanged()) {
            window.onbeforeunload = () => true;
        } else {
            window.onbeforeunload = undefined;
        }
    }

    ///
    public render() {
        if (this.state.formCycle == RequestLifeCycle.Init) {
            return <RequestIndicator type="waiting" />;
        }

        if (this.state.formCycle == RequestLifeCycle.InitFailed) {
            return <RequestErrorReport error={this.state.lastError} />;
        }

        return (
            <Grid container justifyContent="space-around">
                <Grid item xs={12} md={10} lg={8}>

                    {this.state.formCycle == RequestLifeCycle.Posting && <RequestIndicator type="post" />}

                    <RequestErrorDialog
                        open={this.state.formCycle == RequestLifeCycle.PostFailed}
                        error={this.state.lastError}
                        onClose={() => this.setState({ lastError: null, formCycle: RequestLifeCycle.None })}
                    />

                    {this.state.dialog}

                    {this.renderToolbar()}

                    <form noValidate autoComplete="off" >
                        {this.renderFormRules()}
                        {this.renderFormFeedback()}
                        {this.renderRecordInfo()}
                    </form>

                    <Prompt
                        when={this.isRecordChanged()}
                        message={this.props.t("Common.Question.Unsave")}
                    />
                </Grid>
            </Grid>
        );
    }
}

export default withSnackbar(withTranslation()(withStyles(FormStyleClasses)(SetupKnowledgeTestEdit)));